/**
 * CompanyIdentityReconciliationModal — Foundations issue #1.
 *
 * Reconciles divergence between curated public.companies values (the signup
 * tag) and the per-persona free-text answers for the same field. Currently
 * handles two identity fields:
 *
 *   - Company name      (vs Q1 response)
 *   - Industry vertical (vs Q2 response)
 *
 * Renders one section per field that actually has unresolved divergence —
 * a session where only industry diverges shows one section; a fresh session
 * where both diverge shows two. Coordinator picks a canonical value per
 * field, then a single Save call resolves them all in one transaction.
 *
 * Neutral framing per the 2026-05-16 product decision — for Ironclad the
 * signup tag was the right answer despite 4 personas saying otherwise, so
 * UX must not bias toward persona consensus.
 *
 * History: renamed from IndustryReconciliationModal 2026-05-16 when the name
 * field was added.
 */
import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import apiService from '../../../services/api';

const PERSONA_LABELS = {
  c_level_tech: 'C-Level Tech',
  c_level_hr: 'HR',
  legal: 'Legal',
  ceo_board: 'CEO/Board',
};

const OTHER_SENTINEL = '__OTHER__';

// Build the choice list for one field from its state object.
function buildChoices(fieldState) {
  const personaValues = new Map();  // value -> [persona_code]
  for (const r of fieldState.persona_responses || []) {
    if (!r.value) continue;
    if (!personaValues.has(r.value)) personaValues.set(r.value, []);
    personaValues.get(r.value).push(r.persona_code);
  }

  const choices = [];
  if (fieldState.signup_value) {
    choices.push({
      value: fieldState.signup_value,
      key: `signup:${fieldState.signup_value}`,
      label: fieldState.signup_value,
      sublabel: 'Signup tag on your company account',
    });
  }
  for (const [value, personaCodes] of personaValues.entries()) {
    if (value === fieldState.signup_value) continue;  // already shown
    const personaNames = personaCodes.map(c => PERSONA_LABELS[c] || c).join(', ');
    choices.push({
      value,
      key: `persona:${value}`,
      label: value,
      sublabel: `${personaCodes.length} persona${personaCodes.length === 1 ? '' : 's'}: ${personaNames}`,
    });
  }
  choices.push({
    value: OTHER_SENTINEL,
    key: 'other',
    label: 'Something else',
    sublabel: 'Type a value that better fits',
  });
  return choices;
}

function FieldSection({ fieldState, selected, otherValue, onSelect, onOtherChange, saving }) {
  const choices = useMemo(() => buildChoices(fieldState), [fieldState]);

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{
        color: '#a8b2cc', fontSize: '0.78rem', fontWeight: 600,
        textTransform: 'uppercase', letterSpacing: '0.04em',
        marginBottom: 10,
      }}>
        {fieldState.label}
      </div>
      {choices.map(c => {
        const isSelected = selected === c.value;
        return (
          <label
            key={c.key}
            style={{
              display: 'block',
              padding: '12px 14px',
              marginBottom: 6,
              background: isSelected ? 'rgba(96, 165, 250, 0.08)' : 'rgba(255,255,255,0.02)',
              border: `1px solid ${isSelected ? 'rgba(96, 165, 250, 0.4)' : 'rgba(255,255,255,0.08)'}`,
              borderRadius: 8,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.7 : 1,
              transition: 'background 120ms, border-color 120ms',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <input
                type="radio"
                name={`choice-${fieldState.key}`}
                value={c.value}
                checked={isSelected}
                onChange={() => onSelect(c.value)}
                disabled={saving}
                style={{ marginTop: 3, accentColor: '#60a5fa' }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ color: '#e6f1ff', fontSize: '0.92rem', fontWeight: 600 }}>
                  {c.label}
                </div>
                <div style={{ color: '#7a8599', fontSize: '0.76rem', marginTop: 2 }}>
                  {c.sublabel}
                </div>
                {c.value === OTHER_SENTINEL && isSelected && (
                  <input
                    type="text"
                    autoFocus
                    value={otherValue}
                    onChange={(e) => onOtherChange(e.target.value)}
                    placeholder={`e.g., ${fieldState.signup_value || 'something better'}`}
                    maxLength={fieldState.key === 'name' ? 255 : 100}
                    disabled={saving}
                    style={{
                      marginTop: 10, width: '100%',
                      padding: '8px 10px',
                      background: '#0a1019',
                      border: '1px solid rgba(255,255,255,0.12)',
                      borderRadius: 6,
                      color: '#e6f1ff',
                      fontSize: '0.9rem',
                    }}
                  />
                )}
              </div>
            </div>
          </label>
        );
      })}
    </div>
  );
}

export default function CompanyIdentityReconciliationModal({ isOpen, data, onClose, onResolved }) {
  // Per-field UI state — selected value and "other" free-text. Keyed by field key.
  const [selections, setSelections] = useState({});
  const [otherValues, setOtherValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen || !data) return null;

  // Only render sections for fields that actually need resolution. A field
  // already resolved, or one with no divergence, doesn't show.
  const fieldsToShow = Object.values(data.fields || {}).filter(f => f.needs_resolution);
  if (fieldsToShow.length === 0) return null;

  const allFieldsAnswered = fieldsToShow.every(f => {
    const sel = selections[f.key];
    if (sel === undefined) return false;
    if (sel === OTHER_SENTINEL) {
      return (otherValues[f.key] || '').trim().length > 0;
    }
    return true;
  });
  const canSave = allFieldsAnswered && !saving;

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const values = {};
      for (const f of fieldsToShow) {
        const sel = selections[f.key];
        values[f.key] = sel === OTHER_SENTINEL ? otherValues[f.key].trim() : sel;
      }
      const result = await apiService.post(
        `/api/assessments/sessions/${data.session_id}/identity-resolve`,
        { values }
      );
      if (onResolved) onResolved(result);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  const headerCopy = fieldsToShow.length === 1
    ? `Confirm your ${fieldsToShow[0].label.toLowerCase()}`
    : 'Confirm your company details';

  const subCopy = fieldsToShow.length === 1
    ? `Your account and your personas reported different values for ${fieldsToShow[0].label.toLowerCase()}.`
    : `Your account and your personas reported different values for ${fieldsToShow.length} company fields.`;

  return createPortal(
    <>
      <div
        onClick={saving ? undefined : onClose}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(4px)',
          zIndex: 10000,
        }}
      />
      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 10001,
        width: '95%', maxWidth: 640,
        maxHeight: '90vh',
        background: '#0f1923',
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: 16,
        boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }}>
        <div style={{ padding: '24px 24px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h2 style={{ color: '#e6f1ff', fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>
                {headerCopy}
              </h2>
              <p style={{ color: '#7a8599', fontSize: '0.85rem', margin: '4px 0 0', lineHeight: 1.4 }}>
                {subCopy} Risk calculations, benchmarks, and recommendations need one
                canonical answer for each.
              </p>
            </div>
            {!saving && (
              <button
                onClick={onClose}
                aria-label="Close"
                style={{
                  background: 'none', border: 'none', color: '#7a8599',
                  fontSize: '1.2rem', cursor: 'pointer', padding: '4px 8px',
                }}
              >&#10005;</button>
            )}
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px 0' }}>
          {fieldsToShow.map(f => (
            <FieldSection
              key={f.key}
              fieldState={f}
              selected={selections[f.key]}
              otherValue={otherValues[f.key] || ''}
              onSelect={(v) => setSelections(prev => ({ ...prev, [f.key]: v }))}
              onOtherChange={(v) => setOtherValues(prev => ({ ...prev, [f.key]: v }))}
              saving={saving}
            />
          ))}
        </div>

        {error && (
          <div style={{
            margin: '0 24px 16px', padding: '10px 14px',
            background: 'rgba(248,113,113,0.08)',
            border: '1px solid rgba(248,113,113,0.3)',
            borderRadius: 6,
            color: '#f87171', fontSize: '0.85rem',
          }}>
            Couldn&apos;t save: {error}
          </div>
        )}

        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid rgba(255,255,255,0.08)',
          display: 'flex', justifyContent: 'flex-end', gap: 12,
        }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              padding: '8px 16px',
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 6,
              color: '#c0c8d8',
              fontSize: '0.9rem',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.5 : 1,
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave}
            style={{
              padding: '8px 18px',
              background: canSave ? '#60a5fa' : 'rgba(96, 165, 250, 0.3)',
              border: 'none',
              borderRadius: 6,
              color: canSave ? '#0a1019' : '#7a8599',
              fontSize: '0.9rem',
              fontWeight: 600,
              cursor: canSave ? 'pointer' : 'not-allowed',
            }}
          >
            {saving ? 'Saving…' : 'Save & Recalculate'}
          </button>
        </div>
      </div>
    </>,
    document.body
  );
}
